import {Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {delay, finalize, last, switchMap, tap} from 'rxjs/operators';
import {CorpDetails, SchoolStatus, V3School} from "../../model/v3/schools";
import {SchoolV3RestService} from "../../services/rest/v3/school-rest.service";
import {PublicV3RestService} from "../../services/rest/v3/public-rest.service";
import {CountryDefinition, V3Address, V3ContactEntry} from "../../model/v3/people";
import {concat, Observable} from "rxjs";
import {formatDate} from "@angular/common";

enum SaveStatus {
  Save = 'Save',
  Saving = 'Saving',
  Saved = 'Saved'
}
@Component({
    selector: 'app-admin-school-edit',
    templateUrl: './admin-school-edit.component.html',
    styleUrls: ['./admin-school-edit.component.scss'],
    standalone: false
})
export class AdminSchoolEditComponent implements OnInit {

  school: V3School;
  schoolId: number;
  countries: CountryDefinition[];
  oldSchoolIdSaveStatus = SaveStatus.Save;
  schoolDetailsSaveStatus = SaveStatus.Save;
  schoolCorpDetailsSaveStatus = SaveStatus.Save;
  schoolLabelsSaveStatus = SaveStatus.Save;
  schoolStatusDetailsSaveStatus = SaveStatus.Save;
  schoolAddress: V3Address;
  schoolContacts: V3ContactEntry[];
  schoolCorpDetails: CorpDetails;
  schoolAddressSaveStatus = SaveStatus.Save;
  schoolContactsSaveStatus = SaveStatus.Save;

  constructor(
    private route: ActivatedRoute,
    private schoolV3Rest: SchoolV3RestService,
    private publicV3Rest: PublicV3RestService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.publicV3Rest
      .listCountries()
      .pipe(
        tap<CountryDefinition[]>((countries) => (this.countries = countries))
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.route.parent.paramMap
      .pipe(
        tap((params) => (this.schoolId = +params.get('schoolId'))),
        switchMap((_) => this.schoolV3Rest.getSchool(this.schoolId)),
        tap((school) => {
          this.school = school
          this.school.expDate = formatDate(this.school.expDate, "yyyy-MM-dd", this.locale) as unknown as number;
        }),
        switchMap(_ => this.schoolV3Rest.getSchoolAddress(this.schoolId)),
        tap((schoolAddress) => this.schoolAddress = schoolAddress),
        switchMap(_ => this.loadContacts()),
        switchMap(_ => this.schoolV3Rest.getSchoolCorpDetails(this.schoolId)),
        tap((schoolCorpDetails) => this.schoolCorpDetails = schoolCorpDetails)
      ).subscribe();
  }

  loadContacts() {
    return this.schoolV3Rest.listSchoolContacts(this.schoolId).pipe(
      tap((schoolContacts) => {
        this.schoolContacts = schoolContacts as V3ContactEntry[];
        if (this.schoolContacts.length == 0)
          this.addContact()
      }),
    )
  }

  getSchoolStatusList() {
    return Object.keys(SchoolStatus);
  }

  getSchoolStatusName(key: string) {
    return SchoolStatus[key];
  }

  addContact() {
    this.schoolContacts.push({} as V3ContactEntry);
  }

  removeContact(contactIdx: number) {
    let contact = this.schoolContacts[contactIdx];
    if (contact.id != null)
      this.deleteSchoolContact(contact.id)
    if (this.schoolContacts.length == 0)
      this.addContact()

  }

  saveOldSchoolId() {
    this.schoolV3Rest
      .updateSchoolOldId(this.schoolId, this.school.oldId)
      .pipe(
        tap((_) => (this.oldSchoolIdSaveStatus = SaveStatus.Saving)),
        delay(1000),
        finalize(() => {
          this.oldSchoolIdSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.oldSchoolIdSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }

  saveSchoolDetails() {
    this.schoolV3Rest
      .updateSchoolDetails(this.schoolId, this.school.details)
      .pipe(
        tap((_) => (this.schoolDetailsSaveStatus = SaveStatus.Saving)),
        delay(1000),
        finalize(() => {
          this.schoolDetailsSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolDetailsSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }

  saveSchoolAddress() {
    this.schoolV3Rest
      .updateSchoolAddress(this.schoolId, this.schoolAddress)
      .pipe(
        tap((_) => (this.schoolAddressSaveStatus = SaveStatus.Saving)),
        delay(1000),
        finalize(() => {
          this.schoolAddressSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolAddressSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }

  saveSchoolContacts() {
    let observables: Observable<unknown>[] = new Array<Observable<unknown>>();
    this.schoolContacts.forEach(c => {
        c.id == null
          ? observables.push(this.schoolV3Rest.addSchoolContact(this.schoolId, c))
          : observables.push(this.schoolV3Rest.updateSchoolContact(this.schoolId, c.id, c))
      }
    )

    concat(
      ...observables
    )
      .pipe(
        tap((_) => (this.schoolContactsSaveStatus = SaveStatus.Saving)),
        last(),
        switchMap(_ => this.loadContacts()),
        delay(1000),
        finalize(() => {
          this.schoolContactsSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolContactsSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }


  deleteSchoolContact(contactId: number) {
    this.schoolV3Rest
      .deleteSchoolContact(this.schoolId, contactId)
      .pipe(
        tap((_) => (this.schoolContactsSaveStatus = SaveStatus.Saving)),
        switchMap(_ => this.loadContacts()),
        delay(1000),
        finalize(() => {
          this.schoolContactsSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolContactsSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }

  saveCorpDetails() {
    this.schoolV3Rest
      .updateSchoolCorpDetails(this.schoolId, this.schoolCorpDetails)
      .pipe(
        tap((_) => (this.schoolCorpDetailsSaveStatus = SaveStatus.Saving)),
        delay(1000),
        finalize(() => {
          this.schoolCorpDetailsSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolCorpDetailsSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }

  saveSchoolLabels() {
    this.schoolV3Rest
      .updateSchoolLabels(this.schoolId, this.school.labels.split(','))
      .pipe(
        tap((_) => (this.schoolLabelsSaveStatus = SaveStatus.Saving)),
        delay(1000),
        finalize(() => {
          this.schoolLabelsSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolLabelsSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }
  saveSchoolStatusDetails() {
    concat(
      this.schoolV3Rest
        .updateSchoolStatus(
          this.schoolId,
          this.school.status
        ),
      this.schoolV3Rest
        .updateSchoolExpiryDate(
          this.schoolId,
          new Date(this.school.expDate)
        )
    ).pipe(
        tap((_) => (this.schoolStatusDetailsSaveStatus = SaveStatus.Saving)),
        delay(1000),
        finalize(() => {
          this.schoolStatusDetailsSaveStatus = SaveStatus.Saved;
          setTimeout(() => {
            this.schoolStatusDetailsSaveStatus = SaveStatus.Save;
          }, 5000);
        })
      )
      .subscribe();
  }

  isDisabled(status: SaveStatus) {
    return status == SaveStatus.Saving;
  }
}
